import { useEffect, useState } from 'react';
import { CalendarDays, KeyRound, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { googleKalenderApi, type GoogleKalenderEinstellungen } from '../../../lib/api';

const LEER: GoogleKalenderEinstellungen = {
  enabled: false,
  clientId: '',
  clientSecretIsSet: false,
  redirectUri: '',
  connectedTeachers: 0,
  encryptionConfigured: true,
  scopes: [],
};

const CALLBACK_PFAD = '/api/wp/teacher/google-calendar/callback';

/** Google-Kalender fürs Lehrerportal (LEHR-3.4): OAuth-Client und Schalter. */
export default function GoogleCalendarSettings() {
  const [daten, setDaten] = useState<GoogleKalenderEinstellungen>(LEER);
  const [neuerSchluessel, setNeuerSchluessel] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    googleKalenderApi
      .get()
      .then((geladen) => setDaten({ ...LEER, ...geladen }))
      .catch((e: unknown) => toast.error(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false));
  }, []);

  const speichern = async (aenderung: Partial<GoogleKalenderEinstellungen> & { clearClientSecret?: boolean } = {}) => {
    setSpeichert(true);
    try {
      const gespeichert = await googleKalenderApi.update({
        enabled: aenderung.enabled ?? daten.enabled,
        clientId: daten.clientId,
        redirectUri: daten.redirectUri,
        ...(neuerSchluessel.trim() ? { clientSecret: neuerSchluessel.trim() } : {}),
        ...(aenderung.clearClientSecret ? { clearClientSecret: true } : {}),
      });
      setDaten({ ...LEER, ...gespeichert });
      setNeuerSchluessel('');
      toast.success('Google-Kalender-Einstellungen gespeichert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const gesperrt = laedt || speichert;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Google-Kalender</h1>
        <p className="text-muted-foreground">
          Lehrkräfte verbinden im Lehrerportal ihren Google-Kalender: Unterricht geht nach Google, belegte
          Zeiten kommen als Sperrzeiten zurück.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Anbindung
          </CardTitle>
          <CardDescription>
            {daten.connectedTeachers === 1
              ? '1 Lehrkraft ist verbunden.'
              : `${daten.connectedTeachers} Lehrkräfte sind verbunden.`}{' '}
            Abgleich nachts automatisch für die nächsten 8 Wochen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Google-Kalender aktiv</div>
              <div className="text-xs text-muted-foreground">
                Zeigt „Google verbinden“ im Stundenplan des Lehrerportals.
              </div>
            </div>
            <Switch
              aria-label="Google-Kalender aktiv"
              checked={daten.enabled}
              onCheckedChange={(v) => speichern({ enabled: v })}
              disabled={gesperrt}
            />
          </div>

          {!daten.encryptionConfigured && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              Am Server ist keine Verschlüsselung eingerichtet (<code>BILLING_ENCRYPTION_KEY</code>). Ohne sie lässt
              sich der Clientschlüssel nicht speichern.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="google-client-id">Client-ID</Label>
            <Input
              id="google-client-id"
              value={daten.clientId}
              placeholder="1234567890-abc.apps.googleusercontent.com"
              onChange={(e) => setDaten((prev) => ({ ...prev, clientId: e.target.value }))}
              disabled={gesperrt}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="google-client-secret" className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              Clientschlüssel
            </Label>
            <Input
              id="google-client-secret"
              type="password"
              autoComplete="off"
              value={neuerSchluessel}
              placeholder={daten.clientSecretIsSet ? 'Hinterlegt — zum Ersetzen neu eingeben' : 'Noch nicht hinterlegt'}
              onChange={(e) => setNeuerSchluessel(e.target.value)}
              disabled={gesperrt}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Wird verschlüsselt gespeichert und nie wieder angezeigt.</span>
              {daten.clientSecretIsSet && !daten.enabled && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => speichern({ clearClientSecret: true })}
                  disabled={gesperrt}>
                  Schlüssel entfernen
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="google-redirect-uri">Weiterleitungs-URI</Label>
            <Input
              id="google-redirect-uri"
              value={daten.redirectUri}
              placeholder={`https://<website>${CALLBACK_PFAD}`}
              onChange={(e) => setDaten((prev) => ({ ...prev, redirectUri: e.target.value }))}
              disabled={gesperrt}
            />
            <p className="text-xs text-muted-foreground">
              Genau so auch in Google Cloud beim OAuth-Client unter „Autorisierte Weiterleitungs-URIs“ eintragen —
              Adresse der Kunden-Website plus <code>{CALLBACK_PFAD}</code>.
            </p>
          </div>

          {daten.scopes.length > 0 && (
            <div className="space-y-1">
              <div className="text-sm font-medium">Benötigte Bereiche (Scopes)</div>
              <ul className="list-disc pl-5 text-xs text-muted-foreground">
                {daten.scopes.map((scope) => (
                  <li key={scope}>
                    <code>{scope}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => speichern()} disabled={gesperrt}>
              <Save className="mr-2 h-4 w-4" />
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
